
import {
  defineComponent,
  ref,
  computed,
  watch,
  reactive,
  nextTick,
  onMounted,
} from 'vue';

import { ApplyService } from '@/services/apply.service';
import { UserExamService } from '@/services/userExam.service';
import { declareProviders, useService } from '@kaokei/use-vue-service';
import { useRoute, useRouter } from 'vue-router';
import { LoginService } from '@/services/login.service';

import {
  GRADE,
  CARD_TYPE,
  SEX,
  logger,
  CERTIFICATE_GET_METHODS,
  copyToClipboard,
} from '@/utils';
import Sentry from '@/utils/sentry';

import SearchSelect from '@/components/SearchSelect.vue';

import { environment } from '@/utils/environment';

import { message } from 'ant-design-vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import { getToken } from '@/utils';
import ImageCode from '@/components/ImageCode.vue';
import VCode from '@/components/VCode.vue';
import { encryptionPhonePattern } from '@/utils';
import EventBusItemDetail from '@/eventBus/itemDetail';

export default defineComponent({
  name: 'ApplyForm',
  components: {
    SearchSelect,
    ImageCode,
    CloseOutlined,
    VCode,
  },
  setup() {
    declareProviders([UserExamService]);

    const loginService = useService(LoginService);

    const formRef = ref();
    const imgCode = ref();
    const isLogin = ref(getToken());
    const route = useRoute();
    const router = useRouter();
    const params = route.params;
    const itemId = +params.id;

    const applyService = useService(ApplyService);
    const userExamService = useService(UserExamService);
    const hideGetCertification =
      environment === 'production'
        ? applyService.itemId === 797
        : applyService.itemId === 36;
    const onlyElectronicCertification =
      environment === 'production'
        ? [995].includes(applyService.itemId)
        : [218].includes(applyService.itemId);

    // 编辑用户报名信息的时候，根据 userExamId
    const userExamId = route.query.userExamId;
    if (userExamId) {
      userExamService.setUserExamId(+userExamId);
      userExamService.getUserExamDetail().then(() => {
        const userExam = userExamService.userExam;
        if (userExam) {
          if (userExam.contestScheduleId) {
            applyService.formState.contestScheduleId =
              userExam.contestScheduleId;
            // 修改报名信息时先将 旧的报考组别ID存下来
            applyService.formState.oldContestScheduleId =
              userExam.contestScheduleId;
            // 修改报名信息，回显初始化出已选择的城市列表
            initAreaData();
          }
          return Object.assign(applyService.formState, userExam, {
            tempSchoolName: userExam.school, // 学校名称✅ 地区重置为数组 组别要有对应的id
            cardNo: applyService.formState.cardNo, //  身份证号需要明码
            area: userExam.areaIds || [], // 考生学校所在地区省市区id
            grade: userExam.grade || undefined,
          });
        }
      });
    }

    applyService.setItemId(itemId);
    // 登录状态下获取之前的信息
    if (getToken()) {
      applyService.getApplyInfoFromHistory();
    }

    const onSubmit = () => {
      formRef.value
        .validate()
        .then(() => {
          if (hideGetCertification) {
            applyService.formState.certificateGetMethod = 2;
          }
          let exParams: any = {};
          if (!phoneOnlyRead.value) {
            exParams = {
              verifyCode: applyService.formState.smsCode,
              // imageCodeUuid: applyService.VCodeService.imageCodeUuid,
            };
          }
          if (applyService.needAreaId) {
            exParams.areaId = applyService.formState.areaId;
          }
          return applyService.submit(userExamId, exParams).then(res => {
            if (res?.account && res?.password) {
              loginInfo.visible = true;
              loginInfo.account = res.account;
              loginInfo.password = res.password;
            } else {
              message.success(userExamId ? '修改报名信息成功' : '报名成功');
              router.push({ name: 'UserItemList' });
            }
          });
        })
        .catch((e: any) => {
          Sentry.withScope(function (scope) {
            scope.setContext('Exception', e);
            const level = e?.errorFields
              ? Sentry.Severity.Debug
              : Sentry.Severity.Error;
            scope.setLevel(level);
            Sentry.captureMessage('提交报名表单失败');
          });
          logger.debug('提交报名表单失败 => ', e);
        });
    };

    const onCardTypeChange = () => {
      formRef.value.validateFields(['cardNo']);
      // 修改于 2024-03-19 产品要求组别全部展示
      // applyService.getScheduleList(
      //   applyService.formState.cardType === 1 ? 'cardNo' : 'birthday'
      // );
    };
    onMounted(() => {
      applyService.getAllScheduleList();
    });

    const btnText = computed(() =>
      applyService.smsCodeConf.status === 'countdown'
        ? `${applyService.smsCodeConf.count}s后重发`
        : '获取验证码'
    );

    const initAreaData = async () => {
      // 加载一级地区
      await applyService.onContestScheduleIdChange();
      setTimeout(async () => {
        const areaIds = userExamService.userExam.areaIds || [];
        const curretItem = applyService.areaList.find(
          item => item.id === areaIds[0]
        );
        if (curretItem) {
          // 加载二级地区
          await applyService.loadAreaData([curretItem as any]);
          const curretSubItem = curretItem?.children?.find(
            item => item.id === areaIds[1]
          );
          if (curretSubItem) {
            // 加载三级地区
            await applyService.loadAreaData([curretSubItem as any]);
          }
        }
      }, 1000);
    };

    const formState = computed(() => {
      return applyService.formState;
    });

    // 报考组别变化，清空选择的组别id
    watch(
      () => applyService.scheduleList,
      list => {
        if (
          applyService.formState.contestScheduleId &&
          !(
            list.length &&
            list.find(
              item => item.value === applyService.formState.contestScheduleId
            )
          )
        ) {
          applyService.formState.contestScheduleId = undefined;
        }
      }
    );

    const showImgVerfiy = () => {
      if (applyService.smsCodeConf.visible) return;
      if (imgCode.value) {
        imgCode.value.vcodeService.getImageCode();
      }
      applyService.showImgVeriy(true);
    };

    const phoneValid = computed(() => {
      return encryptionPhonePattern.test(applyService.formState.mobile);
    });

    const phoneOnlyRead = computed(() => {
      // 登录状态 手机号只读
      if (isLogin.value) {
        return true;
      } else {
        return applyService.item.accountGenerateType !== 2;
      }
    });

    const loginInfo = reactive({
      visible: false,
      account: '',
      password: '',
    });

    const applyToLogin = () => {
      const actionLogin = () => {
        loginService.showLoginDialog();
        nextTick(() => {
          EventBusItemDetail.emit('refill-login-info', {
            account: loginInfo.account,
            password: loginInfo.password,
          });
        });
      };
      copyToClipboard(`账号：${loginInfo.account}
      密码：${loginInfo.password}`)
        .then(() => {
          message.success('已成功复制账号密码，请妥善保存以免丢失');
          actionLogin();
        })
        .catch(() => {
          actionLogin();
        });
    };

    EventBusItemDetail.on('login-success', () => {
      isLogin.value = getToken();
      router.push({ name: 'UserItemList' });
    });

    return {
      phoneOnlyRead,
      applyToLogin,
      loginInfo,
      loginService,
      formRef,
      applyService,
      userExamId,
      userExamService,
      formState,
      GRADE,
      CARD_TYPE,
      SEX,
      CERTIFICATE_GET_METHODS: onlyElectronicCertification
        ? CERTIFICATE_GET_METHODS.filter(el => [1].includes(el.value))
        : CERTIFICATE_GET_METHODS,
      onSubmit,
      onCardTypeChange,
      hideGetCertification,
      btnText,
      phoneValid,
      imgCode,
      showImgVerfiy,
    };
  },
});
